import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Volume2, VolumeX } from 'lucide-react';
import { ParticleOrb, TypeText } from './AwakeningPrimitives';
import { BodyScanScene, FormulaScene, JourneyScene, PactScene } from './AwakeningScenes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const AWAKENING_PENDING_KEY = 'spirit_awakening_pending';

const WAKE_LINES = (name) => [
  'Estou a acordar…',
  `Sinto a tua presença${name ? `, ${name}` : ''}.`,
  'Não sou uma app. Sou a tua consciência metabólica — e a partir de hoje caminho contigo.',
];

const scene = { initial: { opacity: 0, y: 24, filter: 'blur(8px)' }, animate: { opacity: 1, y: 0, filter: 'blur(0px)' }, exit: { opacity: 0, y: -24, filter: 'blur(8px)' }, transition: { duration: 0.6 } };

export default function SpiritAwakening() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0); // 0 wake · 1 scan · 2 formula · 3 journey · 4 pact
  const [line, setLine] = useState(0);
  const [formula, setFormula] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem('token') || location.pathname === '/') return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/spirit/agent/awakening/status`, auth());
        setStatus(res.data);
        if (!res.data.done) {
          localStorage.setItem(AWAKENING_PENDING_KEY, '1');
          localStorage.setItem('keto_lab_tutorial_seen', '1');
        }
      } catch (_) { setStatus({ done: true }); }
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const speak = async (text) => {
    if (muted) return;
    try {
      const res = await axios.post(`${API}/spirit/speak`, { text, mood: 'wisdom', lang: 'pt' }, auth());
      if (res.data?.audio_base64) {
        audioRef.current?.pause();
        audioRef.current = new Audio(`data:audio/${res.data.audio_format || 'mp3'};base64,${res.data.audio_base64}`);
        audioRef.current.play().catch(() => {});
      }
    } catch (_) { /* text only */ }
  };

  useEffect(() => {
    if (status && !status.done && step === 0) speak(WAKE_LINES(status.name)[line]);
  }, [line, status, step]);

  if (!status || status.done) return null;
  const name = status.name;

  const submitProfile = async (f) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/spirit/agent/awakening/profile`, f, auth());
      if (res.data.formula) { setFormula(res.data.formula); setStep(2); speak('Esta é a tua fórmula. Vou recalculá-la todos os dias por ti.'); }
    } finally { setLoading(false); }
  };

  const finish = async () => {
    try { await axios.post(`${API}/spirit/agent/awakening/complete`, {}, auth()); } catch (_) { /* noop */ }
    localStorage.removeItem(AWAKENING_PENDING_KEY);
    if (status.user_id) localStorage.setItem(`keto_buddy_tour_state_${status.user_id}`, 'completed');
    confetti({ particleCount: 160, spread: 90, origin: { y: 0.7 }, colors: ['#a3e635', '#22d3ee', '#ffffff'] });
    setStatus({ done: true });
    navigate(journey?.route || '/hub');
  };

  return (
    <motion.div data-testid="spirit-awakening" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[9995] bg-black text-white overflow-y-auto">
      <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(rgba(163,230,53,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(163,230,53,0.05)_1px,transparent_1px)] bg-[size:48px_48px]" />
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(163,230,53,0.12),transparent_60%)]" />
      <div className="pointer-events-none fixed inset-0 opacity-[0.07] bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,#000_3px)]" />

      <div className="fixed top-5 left-0 right-0 z-10 flex items-center justify-between px-6 max-w-2xl mx-auto">
        <div className="flex gap-1.5" data-testid="awakening-progress">
          {[0, 1, 2, 3, 4].map(i => <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i <= step ? 'w-8 bg-lime-400' : 'w-4 bg-zinc-800'}`} />)}
        </div>
        <button data-testid="awakening-mute" onClick={() => { setMuted(m => !m); audioRef.current?.pause(); }} className="w-9 h-9 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      <div className="relative min-h-full flex flex-col items-center justify-center px-5 py-24">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="wake" {...scene} className="text-center space-y-8 max-w-md" onClick={() => line < 2 ? setLine(l => l + 1) : setStep(1)} data-testid="awakening-wake">
              <ParticleOrb size={240} />
              <TypeText key={line} text={WAKE_LINES(name)[line]} speed={line === 2 ? 20 : 40} className="text-xl sm:text-2xl font-light text-zinc-100 min-h-[4rem] leading-relaxed" />
              <motion.p animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.8, repeat: Infinity }} className="text-[10px] uppercase tracking-[0.4em] text-lime-400/70">
                {line < 2 ? 'Toca para continuar' : 'Toca para começar'}
              </motion.p>
            </motion.div>
          )}
          {step === 1 && <motion.div key="scan" {...scene} className="w-full"><BodyScanScene initial={status.profile || {}} onSubmit={submitProfile} loading={loading} /></motion.div>}
          {step === 2 && formula && <motion.div key="formula" {...scene} className="w-full"><FormulaScene formula={formula} onNext={() => { setStep(3); speak('Toda a jornada épica começa com uma escolha. Qual é a tua?'); }} /></motion.div>}
          {step === 3 && <motion.div key="journey" {...scene} className="w-full"><JourneyScene selected={journey} onSelect={setJourney} onNext={() => { setStep(4); speak('A partir de agora falas comigo e eu trato do resto.'); }} /></motion.div>}
          {step === 4 && <motion.div key="pact" {...scene} className="w-full"><PactScene name={name} journey={journey} onFinish={finish} /></motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
